/**
 * Admin Header Component
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore, useUIStore } from '../stores';
import { ROLE_LABELS, type UserRole } from '../types';

import { Dropdown, DropdownItem, DropdownDivider, DropdownLabel } from './ui/Dropdown';

export function Header() {
  const navigate = useNavigate();
  const { user, logout, getActiveRole, switchRole } = useAuthStore();
  const { darkMode, toggleDarkMode, notifications } = useUIStore();
  const [_showSearch, setShowSearch] = useState(false);

  const activeRole = getActiveRole();
  const userRoles = user?.roles || [];
  const hasMultipleRoles = userRoles.length > 1;

  const getRoleLabel = (role: string | null | undefined): string => {
    if (!role) return 'Admin';
    return ROLE_LABELS[role as UserRole] || role;
  };

  const handleRoleSwitch = (role: UserRole) => {
    switchRole(role);
    // Optionally navigate to dashboard after role switch
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Quick search shortcut
  const _handleKeyboardShortcut = (e: React.KeyboardEvent) => {
    if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setShowSearch(true);
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Left section - Breadcrumb/Title */}
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Compound Leitstand
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Frankfurt Ost</p>
          </div>
        </div>

        {/* Center - Quick search */}
        <div className="flex-1 max-w-xl px-8">
          <button
            onClick={() => setShowSearch(true)}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <span>Schnellsuche (VIN, Kennzeichen)...</span>
            <kbd className="ml-auto px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 rounded">
              Strg+K
            </kbd>
          </button>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          {/* Live indicator */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-xs font-medium text-green-700 dark:text-green-400">Live</span>
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {darkMode ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          {/* Notifications */}
          <Dropdown
            trigger={
              <button className="relative p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
            }
          >
            <DropdownLabel>Benachrichtigungen</DropdownLabel>
            {notifications.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">Keine neuen Benachrichtigungen</div>
            ) : (
              notifications.slice(0, 5).map((notification) => (
                <DropdownItem key={notification.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{notification.title}</span>
                    {notification.message && (
                      <span className="text-xs text-gray-500">{notification.message}</span>
                    )}
                  </div>
                </DropdownItem>
              ))
            )}
            <DropdownDivider />
            <DropdownItem onClick={() => navigate('/notifications')}>Alle anzeigen</DropdownItem>
          </Dropdown>

          {/* User menu */}
          <Dropdown
            trigger={
              <button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.charAt(0) || 'A'}
                    {user?.lastName?.charAt(0) || ''}
                  </span>
                </div>
                <div className="text-left hidden md:block">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    {getRoleLabel(activeRole)}
                    {hasMultipleRoles && (
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                        />
                      </svg>
                    )}
                  </p>
                </div>
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            }
          >
            <DropdownLabel>Konto</DropdownLabel>
            <DropdownItem
              onClick={() => navigate('/profile')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              }
            >
              Mein Profil
            </DropdownItem>
            <DropdownItem
              onClick={() => navigate('/settings')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              }
            >
              Einstellungen
            </DropdownItem>
            <DropdownItem
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            >
              Hilfe
            </DropdownItem>
            {hasMultipleRoles && (
              <>
                <DropdownDivider />
                <DropdownLabel>Rolle wechseln</DropdownLabel>
                {userRoles.map((role) => (
                  <DropdownItem
                    key={role}
                    onClick={() => handleRoleSwitch(role as UserRole)}
                    icon={
                      role === activeRole ? (
                        <svg
                          className="w-4 h-4 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <span className="w-4 h-4" />
                      )
                    }
                  >
                    <span
                      className={
                        role === activeRole
                          ? 'font-medium text-primary-600 dark:text-primary-400'
                          : ''
                      }
                    >
                      {getRoleLabel(role)}
                    </span>
                  </DropdownItem>
                ))}
              </>
            )}
            <DropdownDivider />
            <DropdownItem
              onClick={handleLogout}
              danger
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              }
            >
              Abmelden
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
